import { Component, type ErrorInfo, type ReactNode } from "react";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { EmptyState } from "../empty-state";

interface Props {
  /** Changing it (e.g. the pathname) clears a caught error, so navigating away recovers. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  resetKey: string;
}

/**
 * Catches render errors in the routed page so one failing page shows a
 * recoverable message inside the app shell instead of unmounting the whole app
 * into a blank screen. The sidebar and header stay usable.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Page crashed while rendering", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex flex-1 items-center justify-center px-8 py-16">
        <EmptyState
          icon={IconAlertTriangle}
          title="Something went wrong on this page."
          description="Your other work is safe. Reload the page to try again, or use the menu to go elsewhere."
          primaryAction={{
            label: "Reload page",
            leftIcon: <IconRefresh size={16} />,
            onClick: () => window.location.reload(),
          }}
        />
      </div>
    );
  }
}
